// Settings → Staff (plan §10.2, §10.3; B-080): the people who sign in at a station with a
// PIN, the roles they hold (each for one branch or all) and whether they can sign in yet.
// Add someone (then give them a PIN), change a name or roles, set or reset a PIN (suggested
// and shown once, or typed), archive and restore, and unlock a station's PIN pad (D-047).

import type { StaffListResponse, StaffMember } from '@enp/domain/api';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  focusRing,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Archive, KeyRound, Pencil, Plus, RotateCcw, Unlock, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { KeyHints, usePageKeys } from '../../../purchasing/parts.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { SettingsBreadcrumb, SettingsFrame } from '../../../stations/page-parts.js';
import {
  ArchiveStaffDialog,
  NoStaffAccess,
  PinDialog,
  StaffDialog,
  UnlockDialog,
  useCanManageStaff,
} from '../../../staff/components.js';
import {
  type StaffFilter,
  invalidateStaff,
  staffQuery,
  staffSearchSchema,
  updateStaff,
} from '../../../staff/queries.js';
import { roleWhere, signInState, staffProblemWords } from '../../../staff/words.js';

export const Route = createFileRoute('/_app/settings/staff')({
  validateSearch: staffSearchSchema,
  component: StaffPage,
});

/** Active | Archived as underline tabs with counts, like every other Admin list (UI8C-7). */
function FilterLink({
  status,
  current,
  children,
}: {
  status: StaffFilter;
  current: StaffFilter;
  children: ReactNode;
}) {
  const active = status === current;
  return (
    <Link
      to="/settings/staff"
      search={status === 'active' ? {} : { status }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '-mb-px inline-flex h-control shrink-0 items-center gap-2 border-b-2 border-transparent px-3 text-body font-medium text-muted hover:text-fg',
        active && 'border-primary text-fg',
        focusRing,
      )}
    >
      {children}
    </Link>
  );
}

function RolesCell({ staff, data }: { staff: StaffMember; data: StaffListResponse }) {
  if (staff.roles.length === 0) return <span className="text-muted">No role</span>;
  const several = data.branches.length > 1 || !data.all_branches;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={`Roles of ${staff.display_name}`}>
      {staff.roles.map((r) => {
        const { role, where } = roleWhere(r, data.branches);
        return (
          <li key={`${r.role_id}|${r.branch_id ?? ''}`}>
            <Badge tone="neutral">
              {role}
              {(several || r.branch_id !== null) && (
                <span className="font-normal text-muted"> · {where}</span>
              )}
            </Badge>
          </li>
        );
      })}
    </ul>
  );
}

function SignInCell({ staff }: { staff: StaffMember }) {
  const state = signInState(staff);
  return (
    <span className="flex flex-col items-start gap-1 leading-tight">
      <StatusBadge status={state.tone}>{state.label}</StatusBadge>
      {staff.pin_updated_at && (
        <span className="text-xs text-muted">
          PIN set <AccountDate value={staff.pin_updated_at} />
        </span>
      )}
    </span>
  );
}

function StaffRow({ staff, data }: { staff: StaffMember; data: StaffListResponse }) {
  const queryClient = useQueryClient();
  // Each dialog opens from its own button, and focus goes back to it on close.
  const [open, setOpen] = useState<'edit' | 'pin' | 'archive' | null>(null);
  const openChange = (which: 'edit' | 'pin' | 'archive') => (o: boolean) =>
    setOpen(o ? which : null);
  const restore = useMutation({
    mutationFn: () => updateStaff(staff.id, { archived: false }),
    onSuccess: async () => {
      await invalidateStaff(queryClient);
      toast.success(`${staff.display_name} restored`, {
        description: 'They can sign in at a station again after its next sync.',
      });
    },
    onError: (error) => {
      const words = staffProblemWords(error, 'They weren’t restored');
      toast.error(words.title, { description: words.explain });
    },
  });
  const archived = staff.archived_at !== null;
  return (
    <TableRow>
      <TableCell className="font-medium text-fg">{staff.display_name}</TableCell>
      <TableCell>
        <RolesCell staff={staff} data={data} />
      </TableCell>
      <TableCell>
        {archived ? (
          <span className="text-sm text-muted">
            Archived {staff.archived_at && <AccountDate value={staff.archived_at} />}
          </span>
        ) : (
          <SignInCell staff={staff} />
        )}
      </TableCell>
      <TableCell className="text-right">
        {!staff.can_manage ? (
          <span className="text-sm text-muted">
            {staff.cannot_manage_reason === 'more_access'
              ? 'Has more access than you'
              : 'Works in other branches too'}
          </span>
        ) : archived ? (
          <Button
            size="sm"
            variant="secondary"
            icon={<RotateCcw aria-hidden />}
            loading={restore.isPending}
            aria-label={`Restore ${staff.display_name}`}
            onClick={() => restore.mutate()}
          >
            Restore
          </Button>
        ) : (
          <span className="inline-flex flex-wrap justify-end gap-2">
            <PinDialog
              staff={staff}
              open={open === 'pin'}
              onOpenChange={openChange('pin')}
              trigger={
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<KeyRound aria-hidden />}
                  aria-label={`${staff.has_pin ? 'Reset the PIN of' : 'Set a PIN for'} ${staff.display_name}`}
                >
                  {staff.has_pin ? 'Reset PIN' : 'Set PIN'}
                </Button>
              }
            />
            <StaffDialog
              data={data}
              staff={staff}
              open={open === 'edit'}
              onOpenChange={openChange('edit')}
              trigger={
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Pencil aria-hidden />}
                  aria-label={`Edit ${staff.display_name}`}
                >
                  Edit
                </Button>
              }
            />
            <ArchiveStaffDialog
              staff={staff}
              open={open === 'archive'}
              onOpenChange={openChange('archive')}
              trigger={
                // Quiet on the list: the dialog asks before anything happens (UI8C-7).
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Archive aria-hidden />}
                  aria-label={`Archive ${staff.display_name}`}
                >
                  Archive
                </Button>
              }
            />
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

/** The list's loading shape: the table's own columns. */
function StaffSkeleton() {
  const cols = 'grid-cols-[1fr_1.4fr_1fr_16rem]';
  return (
    <div role="status" aria-live="polite" className="w-full">
      <span className="sr-only">Loading staff…</span>
      <div className={cn('grid items-center gap-4 border-b border-border px-3 py-3', cols)}>
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-20" />
        <span />
      </div>
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className={cn('grid items-center gap-4 border-b border-border px-3 py-3.5', cols)}
        >
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-5 w-40 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="ml-auto h-control-sm w-56" />
        </div>
      ))}
    </div>
  );
}

function StaffPage() {
  useDocumentTitle('Staff');
  const search = Route.useSearch();
  const status: StaffFilter = search.status ?? 'active';
  const canManage = useCanManageStaff();
  const list = useQuery({ ...staffQuery(status), enabled: canManage === true });
  const data = list.data;
  const [adding, setAdding] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const activeCount = data && status === 'active' ? data.staff.length : undefined;
  // The empty list offers "Add staff" itself: the header doesn't repeat it (UI8C-6).
  const emptyActive = data !== undefined && status === 'active' && data.staff.length === 0;
  usePageKeys({ n: canManage && data ? () => setAdding(true) : undefined });

  return (
    <SettingsFrame>
      <SettingsBreadcrumb page="Staff" />
      <PageHeader
        title="Staff"
        description="The people who sign in at a station with a PIN. Their roles say what they can do there, in one branch or all."
        actions={
          canManage && data ? (
            <>
              {data.stations.length > 0 && (
                <UnlockDialog
                  data={data}
                  open={unlocking}
                  onOpenChange={setUnlocking}
                  trigger={
                    <Button variant="secondary" icon={<Unlock aria-hidden />}>
                      Unlock a station
                    </Button>
                  }
                />
              )}
              {!emptyActive && (
                <StaffDialog
                  data={data}
                  open={adding}
                  onOpenChange={setAdding}
                  trigger={<Button icon={<Plus aria-hidden />}>Add staff</Button>}
                />
              )}
            </>
          ) : undefined
        }
      />
      {canManage === false ? (
        <NoStaffAccess />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b border-border">
            <nav aria-label="Filter staff" className="flex flex-wrap gap-1">
              <FilterLink status="active" current={status}>
                Active{activeCount !== undefined && ` (${activeCount})`}
              </FilterLink>
              <FilterLink status="archived" current={status}>
                Archived{data && ` (${data.archived_count})`}
              </FilterLink>
            </nav>
            <span className="pb-2">
              <KeyHints keys={canManage && data ? [['n', 'add staff']] : []} />
            </span>
          </div>
          {list.isError ? (
            <ErrorPanel
              error={list.error}
              titleAs="h2"
              what="The staff"
              onRetry={() => void list.refetch()}
            />
          ) : !data ? (
            <StaffSkeleton />
          ) : data.staff.length === 0 ? (
            status === 'active' ? (
              <EmptyState
                icon={Users}
                title="No staff yet"
                description="Add the people who work at the stations, give each a role and a PIN, and they can sign in."
                action={
                  <Button icon={<Plus aria-hidden />} onClick={() => setAdding(true)}>
                    Add staff
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Archive}
                title="Nobody archived"
                description="People who left are kept here, so what they did keeps their name. You can restore them."
              />
            )
          ) : (
            <Table>
              <TableCaption className="sr-only">
                {status === 'active' ? 'Staff' : 'Archived staff'}
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>{status === 'active' ? 'Station sign-in' : 'Archived'}</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.staff.map((s) => (
                  <StaffRow key={s.id} staff={s} data={data} />
                ))}
              </TableBody>
            </Table>
          )}
          {data && status === 'active' && (
            <p className="mt-4 text-sm text-muted">
              What each role allows is on the{' '}
              <Link to="/settings/roles" className="font-medium text-fg underline">
                Roles
              </Link>{' '}
              page. Changes reach the stations at their next sync.
            </p>
          )}
        </>
      )}
      {/* The empty list's own button (and "n") opens the same form. */}
      {data && emptyActive && <StaffDialog data={data} open={adding} onOpenChange={setAdding} />}
    </SettingsFrame>
  );
}
